import { createRouter, createWebHistory, type RouteLocationNormalized } from 'vue-router'
import { i18n, appLocaleFor, detectRouteLocale, rememberRouteLocale } from '@/locales'
import type { RouteLocale } from '@/locales'
import { useAuthStore } from '@/stores/auth'
import type { UserRole } from '@/types/api'
import { roleHome } from './role-home'

declare module 'vue-router' {
  interface RouteMeta {
    public?: boolean
    roles?: UserRole[]
    workspace?: 'admin' | 'customer' | 'agent'
  }
}

const localePattern = ':locale(en|es)'

const router = createRouter({
  history: createWebHistory(),
  routes: [
    { path: '/', redirect: () => `/${detectRouteLocale()}/login` },
    {
      path: `/${localePattern}/login`,
      name: 'login',
      component: () => import('@/views/LoginView.vue'),
      meta: { public: true },
    },
    {
      path: `/${localePattern}/password-reset`,
      name: 'password-reset',
      component: () => import('@/views/PasswordResetView.vue'),
      meta: { public: true },
    },
    {
      path: `/${localePattern}/unauthorized`,
      name: 'unauthorized',
      component: () => import('@/views/UnauthorizedView.vue'),
      meta: { public: true },
    },
    {
      path: `/${localePattern}/wholesale/apply`,
      name: 'wholesale-apply',
      component: () => import('@/views/WholesaleApplyView.vue'),
      meta: { public: true },
    },
    {
      path: `/${localePattern}/admin`,
      component: () => import('@/layouts/PortalLayout.vue'),
      meta: { roles: ['SUPER_ADMIN', 'ADMIN'], workspace: 'admin' },
      children: [
        { path: '', redirect: { name: 'admin-dashboard' } },
        {
          path: 'dashboard',
          name: 'admin-dashboard',
          component: () => import('@/views/DashboardView.vue'),
          meta: { roles: ['SUPER_ADMIN', 'ADMIN'], workspace: 'admin' },
        },
        {
          path: 'account/security',
          name: 'admin-security',
          component: () => import('@/views/SecurityView.vue'),
          meta: { roles: ['SUPER_ADMIN', 'ADMIN'], workspace: 'admin' },
        },
        {
          path: 'settings',
          name: 'admin-settings',
          component: () => import('@/views/AdminSettingsView.vue'),
          meta: { roles: ['SUPER_ADMIN', 'ADMIN'], workspace: 'admin' },
        },
        {
          path: 'applications',
          name: 'admin-applications',
          component: () => import('@/views/AdminApplicationsView.vue'),
          meta: { roles: ['SUPER_ADMIN', 'ADMIN'], workspace: 'admin' },
        },
        {
          path: 'customers',
          name: 'admin-customers',
          component: () => import('@/views/AdminCustomersView.vue'),
          meta: { roles: ['SUPER_ADMIN', 'ADMIN'], workspace: 'admin' },
        },
        {
          path: 'products',
          name: 'admin-products',
          component: () => import('@/views/AdminProductsView.vue'),
          meta: { roles: ['SUPER_ADMIN', 'ADMIN'], workspace: 'admin' },
        },
        {
          path: 'pricing',
          name: 'admin-pricing',
          component: () => import('@/views/AdminPricingView.vue'),
          meta: { roles: ['SUPER_ADMIN', 'ADMIN'], workspace: 'admin' },
        },
        {
          path: 'agents',
          name: 'admin-agents',
          component: () => import('@/views/AdminAgentsView.vue'),
          meta: { roles: ['SUPER_ADMIN', 'ADMIN'], workspace: 'admin' },
        },
        {
          path: 'commissions',
          name: 'admin-commissions',
          component: () => import('@/views/AdminCommissionsView.vue'),
          meta: { roles: ['SUPER_ADMIN', 'ADMIN'], workspace: 'admin' },
        },
        {
          path: 'quotes',
          name: 'admin-quotes',
          component: () => import('@/views/AdminSalesView.vue'),
          props: { kind: 'quotes' },
          meta: { roles: ['SUPER_ADMIN', 'ADMIN'], workspace: 'admin' },
        },
        {
          path: 'quotes/:quotePublicId',
          name: 'admin-quote-detail',
          component: () => import('@/views/AdminSalesView.vue'),
          props: { kind: 'quotes' },
          meta: { roles: ['SUPER_ADMIN', 'ADMIN'], workspace: 'admin' },
        },
        {
          path: 'orders',
          name: 'admin-orders',
          component: () => import('@/views/AdminSalesView.vue'),
          props: { kind: 'orders' },
          meta: { roles: ['SUPER_ADMIN', 'ADMIN'], workspace: 'admin' },
        },
        {
          path: 'orders/:orderPublicId',
          name: 'admin-order-detail',
          component: () => import('@/views/AdminSalesView.vue'),
          props: { kind: 'orders' },
          meta: { roles: ['SUPER_ADMIN', 'ADMIN'], workspace: 'admin' },
        },
        {
          path: 'invoices',
          name: 'admin-invoices',
          component: () => import('@/views/AdminSalesView.vue'),
          props: { kind: 'invoices' },
          meta: { roles: ['SUPER_ADMIN', 'ADMIN'], workspace: 'admin' },
        },
        {
          path: 'invoices/:invoicePublicId',
          name: 'admin-invoice-detail',
          component: () => import('@/views/AdminSalesView.vue'),
          props: { kind: 'invoices' },
          meta: { roles: ['SUPER_ADMIN', 'ADMIN'], workspace: 'admin' },
        },
      ],
    },
    {
      path: `/${localePattern}/customer`,
      component: () => import('@/layouts/PortalLayout.vue'),
      meta: { roles: ['WHOLESALE_CUSTOMER'], workspace: 'customer' },
      children: [
        { path: '', redirect: { name: 'customer-dashboard' } },
        {
          path: 'dashboard',
          name: 'customer-dashboard',
          component: () => import('@/views/DashboardView.vue'),
          meta: { roles: ['WHOLESALE_CUSTOMER'], workspace: 'customer' },
        },
        {
          path: 'account/security',
          name: 'customer-security',
          component: () => import('@/views/SecurityView.vue'),
          meta: { roles: ['WHOLESALE_CUSTOMER'], workspace: 'customer' },
        },
        {
          path: 'catalog',
          name: 'customer-catalog',
          component: () => import('@/views/CustomerCatalogView.vue'),
          meta: { roles: ['WHOLESALE_CUSTOMER'], workspace: 'customer' },
        },
        {
          path: 'catalog/:productPublicId',
          name: 'customer-product',
          component: () => import('@/views/CustomerProductView.vue'),
          meta: { roles: ['WHOLESALE_CUSTOMER'], workspace: 'customer' },
        },
        {
          path: 'quotes',
          name: 'customer-quotes',
          component: () => import('@/views/CustomerQuotesView.vue'),
          meta: { roles: ['WHOLESALE_CUSTOMER'], workspace: 'customer' },
        },
        {
          path: 'quotes/new',
          name: 'customer-quote-new',
          component: () => import('@/views/CustomerQuoteNewView.vue'),
          meta: { roles: ['WHOLESALE_CUSTOMER'], workspace: 'customer' },
        },
        {
          path: 'quotes/:quotePublicId',
          name: 'customer-quote-detail',
          component: () => import('@/views/CustomerQuotesView.vue'),
          meta: { roles: ['WHOLESALE_CUSTOMER'], workspace: 'customer' },
        },
        {
          path: 'orders',
          name: 'customer-orders',
          component: () => import('@/views/CustomerOrdersView.vue'),
          meta: { roles: ['WHOLESALE_CUSTOMER'], workspace: 'customer' },
        },
        {
          path: 'orders/new',
          name: 'customer-order-new',
          component: () => import('@/views/CustomerOrderNewView.vue'),
          meta: { roles: ['WHOLESALE_CUSTOMER'], workspace: 'customer' },
        },
        {
          path: 'orders/:orderPublicId',
          name: 'customer-order-detail',
          component: () => import('@/views/CustomerOrdersView.vue'),
          meta: { roles: ['WHOLESALE_CUSTOMER'], workspace: 'customer' },
        },
        {
          path: 'invoices',
          name: 'customer-invoices',
          redirect: (to) => ({
            name: 'customer-orders',
            params: { locale: to.params.locale },
          }),
        },
        {
          path: 'invoices/:invoicePublicId',
          name: 'customer-invoice-detail',
          redirect: (to) => ({
            name: 'customer-orders',
            params: { locale: to.params.locale },
          }),
        },
        {
          path: 'company',
          name: 'customer-company',
          component: () => import('@/views/CustomerCompanyView.vue'),
          meta: { roles: ['WHOLESALE_CUSTOMER'], workspace: 'customer' },
        },
        {
          path: 'addresses',
          name: 'customer-addresses',
          redirect: (to) => ({
            name: 'customer-company',
            params: { locale: to.params.locale },
          }),
        },
      ],
    },
    {
      path: `/${localePattern}/agent`,
      component: () => import('@/layouts/PortalLayout.vue'),
      meta: { roles: ['SALES_AGENT'], workspace: 'agent' },
      children: [
        { path: '', redirect: { name: 'agent-dashboard' } },
        {
          path: 'dashboard',
          name: 'agent-dashboard',
          component: () => import('@/views/DashboardView.vue'),
          meta: { roles: ['SALES_AGENT'], workspace: 'agent' },
        },
        {
          path: 'account/security',
          name: 'agent-security',
          component: () => import('@/views/SecurityView.vue'),
          meta: { roles: ['SALES_AGENT'], workspace: 'agent' },
        },
        {
          path: 'customers',
          name: 'agent-customers',
          component: () => import('@/views/AgentWorkspaceView.vue'),
          props: { kind: 'customers' },
          meta: { roles: ['SALES_AGENT'], workspace: 'agent' },
        },
        {
          path: 'customers/:customerPublicId',
          name: 'agent-customer-detail',
          component: () => import('@/views/AgentWorkspaceView.vue'),
          props: { kind: 'customers' },
          meta: { roles: ['SALES_AGENT'], workspace: 'agent' },
        },
        {
          path: 'orders',
          name: 'agent-orders',
          component: () => import('@/views/AgentWorkspaceView.vue'),
          props: { kind: 'orders' },
          meta: { roles: ['SALES_AGENT'], workspace: 'agent' },
        },
        {
          path: 'orders/:orderPublicId',
          name: 'agent-order-detail',
          component: () => import('@/views/AgentWorkspaceView.vue'),
          props: { kind: 'orders' },
          meta: { roles: ['SALES_AGENT'], workspace: 'agent' },
        },
        {
          path: 'sales',
          name: 'agent-sales',
          component: () => import('@/views/AgentWorkspaceView.vue'),
          props: { kind: 'sales' },
          meta: { roles: ['SALES_AGENT'], workspace: 'agent' },
        },
        {
          path: 'commissions',
          name: 'agent-commissions',
          component: () => import('@/views/AgentWorkspaceView.vue'),
          props: { kind: 'commissions' },
          meta: { roles: ['SALES_AGENT'], workspace: 'agent' },
        },
        {
          path: 'statements',
          name: 'agent-statements',
          component: () => import('@/views/AgentWorkspaceView.vue'),
          props: { kind: 'statements' },
          meta: { roles: ['SALES_AGENT'], workspace: 'agent' },
        },
        {
          path: 'statements/:statementPublicId',
          name: 'agent-statement-detail',
          component: () => import('@/views/AgentWorkspaceView.vue'),
          props: { kind: 'statements' },
          meta: { roles: ['SALES_AGENT'], workspace: 'agent' },
        },
        {
          path: 'referral-codes',
          name: 'agent-referral-codes',
          component: () => import('@/views/AgentWorkspaceView.vue'),
          props: { kind: 'referrals' },
          meta: { roles: ['SALES_AGENT'], workspace: 'agent' },
        },
      ],
    },
    { path: '/:pathMatch(.*)*', redirect: () => `/${detectRouteLocale()}/login` },
  ],
  scrollBehavior: () => ({ top: 0 }),
})

router.beforeEach(async (to: RouteLocationNormalized) => {
  const locale = (to.params.locale === 'es' ? 'es' : 'en') as RouteLocale
  i18n.global.locale.value = appLocaleFor(locale)
  rememberRouteLocale(locale)

  const auth = useAuthStore()
  if (!auth.initialized) {
    await auth.bootstrap()
  }

  if (to.meta.public) {
    if (to.name === 'login' && auth.user) {
      return roleHome(auth.user, locale)
    }
    return true
  }

  if (!auth.user) {
    return { name: 'login', params: { locale } }
  }

  const allowedRoles = to.meta.roles ?? []
  if (allowedRoles.length > 0 && !auth.user.roles.some((role) => allowedRoles.includes(role))) {
    return { name: 'unauthorized', params: { locale } }
  }
  return true
})

export default router
